import { api } from './api';
import { grundHeader } from '../components/ui/loeschen-mit-grund-dialog';

export interface UnterrichtseinheitDTO {
  id: number;
  course_id?: number | null;
  rule_id?: number | null;
  override_id?: number | null;
  lehrer_id: number;
  schueler_id: number;
  fach_id: number;
  raum_id?: number | null;
  start_zeit: string;
  end_zeit: string;
  status: 'geplant' | 'durchgeführt' | 'nicht_erschienen' | 'abgesagt' | 'verschoben' | 'abgeschlossen';
  source_type?: 'course' | 'trial';
  kontingent_id?: number | null;
  notizen?: string | null;
  /** Strukturierte Teilnehmerliste (optional, vom Backend geliefert) */
  teilnehmer?: Array<{ subprofileId: string; wpUserId: number; subIndex: number; name: string }> | null;
  // Erweiterte Felder aus JOINs
  kurs_name?: string | null;
  kurs_farbe?: string | null;
  kurs_is_placeholder?: boolean | number | null;
  kurs_placeholder_source?: string | null;
  fach_name?: string | null;
  raum_name?: string | null;
  standort_name?: string | null;
  standort_adresse?: string | null;
  lehrer_name?: string | null;
  teilnehmer_namen?: string | null;
}

export interface UnterrichtseinheitenFilters {
  teacherId?: string | number;
  standortId?: number;
  start?: string; // YYYY-MM-DD
  end?: string;   // YYYY-MM-DD
}

export interface TeacherBlockedTimeDTO {
  id: number;
  teacherId: number;
  source: string;
  sourceEventId?: string | null;
  date?: string | null;
  startTime?: string | null;
  endTime?: string | null;
  isBlocked: boolean;
  isPrivateExternal?: boolean;
  title?: string | null;
  location?: string | null;
  description?: string | null;
  attendees?: Array<{ email?: string; displayName?: string; self?: boolean }> | null;
}

export const unterrichtseinheitenApi = {
  list: async (filters: UnterrichtseinheitenFilters = {}): Promise<UnterrichtseinheitDTO[]> => {
    const params = new URLSearchParams();
    if (filters.teacherId !== undefined) params.set('teacherId', String(filters.teacherId));
    if (filters.standortId !== undefined) params.set('standort_id', String(filters.standortId));
    if (filters.start) params.set('start', filters.start);
    if (filters.end) params.set('end', filters.end);
    const qs = params.toString();
    const url = '/api/unterrichtseinheiten' + (qs ? '?' + qs : '');
    return api.get<UnterrichtseinheitDTO[]>(url);
  },
  create: async (payload: Partial<UnterrichtseinheitDTO>): Promise<UnterrichtseinheitDTO> => {
    return api.post<UnterrichtseinheitDTO>('/api/unterrichtseinheiten', payload);
  },
  update: async (id: number, payload: Partial<UnterrichtseinheitDTO>): Promise<UnterrichtseinheitDTO> => {
    return api.put<UnterrichtseinheitDTO>(`/api/unterrichtseinheiten/${id}`, payload);
  },
  delete: async (id: number, grund: string): Promise<{ message?: string }> => {
    return api.delete<{ message?: string }>(`/api/unterrichtseinheiten/${id}`, grundHeader(grund));
  },
  /**
   * Status für einen einzelnen Kurstermin — auch wenn er bisher nur als Muster
   * besteht; das Backend legt die Einheit dann an (T-0083).
   */
  setOccurrenceStatus: async (
    courseId: number,
    date: string,
    status: UnterrichtseinheitDTO['status'],
  ): Promise<UnterrichtseinheitDTO> => {
    return api.post<UnterrichtseinheitDTO>(`/api/kurse/${courseId}/termine/status`, { date, status });
  },
};

/**
 * Die Einheiten-ID hinter einem Stundenplan-Eintrag. Der Stundenplan führt
 * Einheiten als „unit-123“, der Kalender als „123“; Mustertermine haben keine.
 */
export const unitIdOf = (id: string | number | null | undefined): number | null => {
  const text = String(id ?? '');
  const match = /^(?:unit-)?(\d+)$/.exec(text);
  const value = match ? Number(match[1]) : NaN;
  return Number.isFinite(value) && value > 0 ? value : null;
};

export const teacherBlockedTimesApi = {
  list: async (filters: { teacherId: string | number; start: string; end: string }): Promise<TeacherBlockedTimeDTO[]> => {
    const params = new URLSearchParams();
    params.set('teacherId', String(filters.teacherId));
    params.set('start', filters.start);
    params.set('end', filters.end);
    const url = `/api/teacher-blocked-times?${params.toString()}`;
    const res = await api.get<{ items?: TeacherBlockedTimeDTO[] } | TeacherBlockedTimeDTO[]>(url);
    return Array.isArray(res) ? res : (res.items || []);
  },
};

export interface LehrerOption {
  id: number;
  wp_user_id: number;
  vorname: string;
  nachname: string;
  email?: string | null;
  telefon?: string | null;
  aktiv: boolean;
}

export const lehrerApi = {
  list: async (opts?: { fachId?: number; standortId?: number; limit?: number; offset?: number }): Promise<LehrerOption[]> => {
    const params = new URLSearchParams();
    if (opts?.fachId) params.set('fachId', String(opts.fachId));
    if (opts?.standortId) params.set('standortId', String(opts.standortId));
    params.set('limit', String(opts?.limit ?? 500));
    if (opts?.offset !== undefined) params.set('offset', String(opts.offset));
    const qs = params.toString();
    const url = '/api/lehrer' + (qs ? '?' + qs : '');
    return api.get<LehrerOption[]>(url);
  },
  get: async (id: number): Promise<LehrerOption> => {
    return api.get<LehrerOption>(`/api/lehrer/${id}`);
  },
  getAvailableFaecher: async (teacherId: number): Promise<Array<{ id: number; name: string; beschreibung?: string | null; aktiv: boolean }>> => {
    try {
      return await api.get<Array<{ id: number; name: string; beschreibung?: string | null; aktiv: boolean }>>(`/api/lehrer/${teacherId}/available-faecher`);
    } catch (error: any) {
      const message = String(error?.message || '');
      const isNotFound = message.includes('404') || message.toLowerCase().includes('not found');
      if (!isNotFound) throw error;
      // Fallback für Deployments, in denen /api bereits auf Backend-Root gemappt ist.
      return await api.get<Array<{ id: number; name: string; beschreibung?: string | null; aktiv: boolean }>>(`/lehrer/${teacherId}/available-faecher`);
    }
  }
};


